"use client";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./LiveLecture.css";

import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getDay,
  isValid,
  parse,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { type FC, useEffect, useState, useMemo } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { getEventOptions, getEvents } from "@/api/liveLecture";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/atoms/Select";
import { Button } from "@/components/atoms/Button";
import type {
  Event,
  EventDetailsResponse,
  EventFormOptions,
  Filters,
  LiveLectureProps,
} from "@/types/liveLecture";
import { cn } from "@/lib/utils";

const CALENDAR_VIEWS = {
  MONTH: "month",
  DAY: "day",
};

const NAVIGATE = {
  NEXT: "NEXT",
  PREV: "PREV",
};

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const LiveLecture1: FC<LiveLectureProps> = ({
  data,
  handleTooltipContent: externalHandleTooltipContent,
  handleSelectedEventId: externalHandleSelectedEventId,
  tooltipContent: externalTooltipContent,
  selectedEventId: externalSelectedEventId,
}) => {
  // Extract styling data from props
  const stylingData = data?.data || {};
  const {
    title,
    title_text_color,
    component_background_color,
    day_section_color,
    day_text_color,
    view_button_text_color,
    view_button_bg_color,
    view_button_active_bg_color,
    view_button_active_text_color,
    active_bar_color,
    navigate_button_color,
    left_navigate_icon,
    right_navigate_icon,
    button_icon,
    button_text,
    button_text_color,
    button_bg_color,
    event_color,
    event_text_color,
    tooltip_color,
    tooltip_text_color,
    description,
    content,
  } = stylingData;

  const [filters, setFilters] = useState<Filters>({
    courseName: "",
    semYear: "",
    batch: "",
  });
  const [view, setView] = useState(CALENDAR_VIEWS.MONTH);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [eventFormOptions, setEventFormOptions] = useState<EventFormOptions>(
    {} as EventFormOptions
  );
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const isMobile = useIsMobile();

  // Internal state for tooltip management
  const [internalTooltipContent, setInternalTooltipContent] = useState<
    EventDetailsResponse | Record<string, unknown>
  >({});
  const [internalSelectedEventId, setInternalSelectedEventId] =
    useState<string>("");
  const [hoveredEventId, setHoveredEventId] = useState<string>("");

  // Use external props if provided, otherwise use internal state
  const tooltipContent = externalTooltipContent ?? internalTooltipContent;
  const selectedEventId = externalSelectedEventId ?? internalSelectedEventId;

  // Define handlers - use external if provided, otherwise use internal state setters
  const handleTooltipContent = (
    content: EventDetailsResponse | Record<string, unknown>
  ) => {
    if (externalHandleTooltipContent) {
      externalHandleTooltipContent(content);
    } else {
      setInternalTooltipContent(content);
    }
  };

  const handleSelectedEventId = (id: string) => {
    if (externalHandleSelectedEventId) {
      externalHandleSelectedEventId(id);
    } else {
      setInternalSelectedEventId(id);
    }
  };

  let eventId = 0;

  const getStartAndEndDates = (date: Date) => {
    const startDate = startOfMonth(date);
    const endDate = endOfMonth(date);
    const customStartDate = subDays(startDate, 15);
    const customEndDate = addDays(endDate, 15);
    const formattedStartDate = format(customStartDate, "yyyy-MM-dd");
    const formattedEndDate = format(customEndDate, "yyyy-MM-dd");
    return { formattedStartDate, formattedEndDate };
  };

  const fetchEventsForRange = async (startDate: string, endDate: string) => {
    try {
      const eventData = await getEvents(startDate, endDate);

      // Ensure we always return an array
      if (Array.isArray(eventData)) {
        return eventData;
      }
      // Handle case where API returns wrapped data
      if (
        eventData &&
        typeof eventData === "object" &&
        "data" in eventData &&
        Array.isArray(eventData.data)
      ) {
        return eventData.data;
      }
      if (
        eventData &&
        typeof eventData === "object" &&
        "events" in eventData &&
        Array.isArray(eventData.events)
      ) {
        return eventData.events;
      }
      return [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      const eventOptions = await getEventOptions();

      setEventFormOptions(eventOptions || {});

      const currentDate = new Date();
      const { formattedStartDate, formattedEndDate } =
        getStartAndEndDates(currentDate);

      const eventData = await fetchEventsForRange(
        formattedStartDate,
        formattedEndDate
      );

      // Ensure eventData is always an array
      const safeEventData = Array.isArray(eventData) ? eventData : [];
      setEvents(safeEventData);
      setFilteredEvents(safeEventData);
    };
    fetchInitialData();
  }, []);

  // Set row height for day view using DOM manipulation
  useEffect(() => {
    if (view === CALENDAR_VIEWS.DAY) {
      const setTimeSlotHeights = () => {
        const timeSlots = document.querySelectorAll(
          ".rbc-day-view .rbc-time-slot"
        );
        timeSlots.forEach((slot) => {
          const element = slot as HTMLElement;
          element.style.minHeight = "44px";
          element.style.height = "44px";
          element.style.flex = "0 0 44px";
          element.style.borderTop = "none";
          element.style.borderBottom = "none";
          element.style.borderLeft = "none";
          element.style.borderRight = "none";
          element.style.border = "none";
        });

        // Also target time content rows
        const timeContentRows = document.querySelectorAll(
          ".rbc-day-view .rbc-time-content > div"
        );
        timeContentRows.forEach((row) => {
          const element = row as HTMLElement;
          element.style.minHeight = "44px";
          element.style.height = "44px";
          element.style.borderTop = "none";
          element.style.borderBottom = "none";
          element.style.border = "none";
        });

        // Target all divs within time content
        const timeContentDivs = document.querySelectorAll(
          ".rbc-day-view .rbc-time-content > div > div"
        );
        timeContentDivs.forEach((div) => {
          const element = div as HTMLElement;
          element.style.borderTop = "none";
          element.style.borderBottom = "none";
          element.style.border = "none";
        });

        // Target table rows if they exist
        const tableRows = document.querySelectorAll(
          ".rbc-day-view table tbody tr"
        );
        tableRows.forEach((row) => {
          const element = row as HTMLElement;
          element.style.height = "44px";
          element.style.minHeight = "44px";
          element.style.borderTop = "none";
          element.style.borderBottom = "none";
          element.style.border = "none";
        });

        // Target table cells
        const tableCells = document.querySelectorAll(
          ".rbc-day-view table tbody tr td"
        );
        tableCells.forEach((cell) => {
          const element = cell as HTMLElement;
          element.style.borderTop = "none";
          element.style.borderBottom = "none";
          element.style.border = "none";
        });
      };

      // Set heights immediately
      setTimeSlotHeights();

      // Use MutationObserver to watch for DOM changes
      const observer = new MutationObserver(() => {
        setTimeSlotHeights();
      });

      const calendarContainer = document.querySelector(
        ".live-lecture-calendar"
      );
      if (calendarContainer) {
        observer.observe(calendarContainer, {
          childList: true,
          subtree: true,
        });
      }

      // Also use setTimeout as fallback
      const timeoutId = setTimeout(setTimeSlotHeights, 100);

      return () => {
        observer.disconnect();
        clearTimeout(timeoutId);
      };
    }
  }, [view, currentDate]);

  // Clear tooltip on scroll
  useEffect(() => {
    const handleScroll = () => {
      // Clear hover-based tooltip (desktop)
      if (hoveredEventId) {
        setHoveredEventId("");
        handleTooltipContent({});
      }
      // Clear touch-based tooltip (mobile)
      if (selectedEventId) {
        handleSelectedEventId("");
        handleTooltipContent({});
        setTooltipPosition({ x: 0, y: 0 });
      }
    };

    // Track all containers with listeners attached
    const allContainers = new Set<Element>();

    // Function to attach scroll listeners to all scrollable containers
    const attachScrollListeners = () => {
      const calendarContainer = document.querySelector(".live-lecture-calendar");
      const monthView = document.querySelector(".rbc-month-view");
      const dayView = document.querySelector(".rbc-day-view");
      const timeContent = document.querySelector(".rbc-time-content");
      
      // Add scroll listeners to all found containers
      const containers = [
        calendarContainer,
        monthView,
        dayView,
        timeContent,
      ].filter(Boolean) as Element[];

      containers.forEach((container) => {
        if (!allContainers.has(container)) {
          container.addEventListener("scroll", handleScroll, {
            passive: true,
          });
          allContainers.add(container);
        }
      });
    };

    // Attach listeners immediately
    attachScrollListeners();

    // Also listen to window scroll
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Re-attach listeners after a short delay to catch dynamically created containers
    const timeoutId = setTimeout(() => {
      attachScrollListeners();
    }, 100);

    return () => {
      // Remove listeners from all tracked containers
      allContainers.forEach((container) => {
        container.removeEventListener("scroll", handleScroll);
      });
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timeoutId);
    };
  }, [hoveredEventId, selectedEventId, handleTooltipContent, handleSelectedEventId, view]);

  // Use native mouse move listener for more reliable detection
  // Only enable on desktop (not mobile) since hover doesn't work on mobile
  useEffect(() => {
    if (!hoveredEventId || isMobile) return;

    const handleNativeMouseMove = (e: MouseEvent) => {
      // Check if hoveredEventId is still valid (might have been cleared by mouseleave)
      if (!hoveredEventId) {
        return;
      }

      // Only check if mouse is over the calendar container
      const calendarContainer = document.querySelector(
        ".live-lecture-calendar"
      );
      const target = e.target as HTMLElement;

      if (!calendarContainer || !calendarContainer.contains(target)) {
        // Mouse is outside calendar, clear tooltip immediately
        setHoveredEventId("");
        handleTooltipContent({});
        setTooltipPosition({ x: 0, y: 0 });
        return;
      }

      // Check the element directly under the cursor
      const elementUnderCursor = document.elementFromPoint(
        e.clientX,
        e.clientY
      );

      if (!elementUnderCursor) {
        setHoveredEventId("");
        handleTooltipContent({});
        setTooltipPosition({ x: 0, y: 0 });
        return;
      }

      // Skip if element under cursor is not part of the calendar event structure
      // This helps avoid false positives from tooltip or other overlay elements

      // Get the hovered event element using the current hoveredEventId from closure
      const currentHoveredEventId = hoveredEventId;
      const hoveredEventElement = document.getElementById(
        currentHoveredEventId
      );

      // Check if we're over the specific hovered event element
      let isOverHoveredEvent = false;
      if (hoveredEventElement) {
        // Check if the element under cursor is the event itself or a child of it
        isOverHoveredEvent =
          elementUnderCursor === hoveredEventElement ||
          elementUnderCursor.id === currentHoveredEventId ||
          hoveredEventElement.contains(elementUnderCursor);
      }

      // If still over the hovered event, update tooltip position to follow cursor
      if (isOverHoveredEvent) {
        const adjustedPosition = calculateTooltipPosition(
          e.clientX,
          e.clientY,
          isMobile,
          false // Not selected for hover
        );
        setTooltipPosition(adjustedPosition);
        return;
      }

      // If not over the hovered event, clear the tooltip immediately
      setHoveredEventId("");
      handleTooltipContent({});
      setTooltipPosition({ x: 0, y: 0 });
    };

    // Use capture phase to catch all mouse movements
    document.addEventListener("mousemove", handleNativeMouseMove, true);

    return () => {
      document.removeEventListener("mousemove", handleNativeMouseMove, true);
    };
  }, [hoveredEventId, handleTooltipContent, isMobile]);

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters((prevFilters: Filters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
  };

  // Extract filtering logic into a reusable function
  const applyFilters = (eventsToFilter: Event[]) => {
    const validFilterKeys = Object.keys(filters).filter(
      (key) =>
        filters[key as keyof Filters] !== "" &&
        filters[key as keyof Filters] !== "SELECT"
    );
    if (validFilterKeys.length === 0) {
      return eventsToFilter;
    }

    return eventsToFilter.filter((event) => {
      const { eventDetails } = event;
      if (validFilterKeys.length === 1) {
        const key = validFilterKeys[0];
        return (
          key &&
          eventDetails[key as keyof typeof eventDetails] ===
            filters[key as keyof Filters]
        );
      }
      if (validFilterKeys.length === 2) {
        const firstKey = validFilterKeys[0];
        const secondKey = validFilterKeys[1];
        return (
          firstKey &&
          secondKey &&
          eventDetails[firstKey as keyof typeof eventDetails] ===
            filters[firstKey as keyof Filters] &&
          eventDetails[secondKey as keyof typeof eventDetails] ===
            filters[secondKey as keyof Filters]
        );
      }
      return (
        eventDetails.courseName === filters.courseName &&
        eventDetails.semYear === filters.semYear &&
        eventDetails.batch === filters.batch
      );
    });
  };

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    const filteredValues = applyFilters(events);
    setFilteredEvents(filteredValues);
  };

  const handleViewChange = async (newView: string) => {
    if (newView === CALENDAR_VIEWS.DAY) {
      const date = new Date(currentDate);
      setCurrentDate(startOfMonth(date));
    }
    setView(newView);
    const { formattedStartDate, formattedEndDate } =
      getStartAndEndDates(currentDate);
    const eventData = await fetchEventsForRange(
      formattedStartDate,
      formattedEndDate
    );
    // Ensure eventData is always an array
    const safeEventData = Array.isArray(eventData) ? eventData : [];
    setEvents(safeEventData);
    // Reapply filters if any are active
    const filteredValues = applyFilters(safeEventData);
    setFilteredEvents(filteredValues);
  };

  const getNavigateByDate = (
    view: string
  ): Record<string, (currentDate: Date) => Date> => {
    const navigateByDay = {
      PREV: (currentDate: Date) => addDays(currentDate, -1),
      NEXT: (currentDate: Date) => addDays(currentDate, 1),
      TODAY: () => startOfToday(),
    };

    const navigateByMonth = {
      PREV: (currentDate: Date) => subMonths(currentDate, 1),
      NEXT: (currentDate: Date) => addMonths(currentDate, 1),
      TODAY: () => startOfToday(),
    };

    return view === CALENDAR_VIEWS.DAY ? navigateByDay : navigateByMonth;
  };

  const handleNavigate = async (action: string) => {
    const dateManipulation = getNavigateByDate(view);
    const newDate = dateManipulation[action]
      ? dateManipulation[action](currentDate)
      : currentDate;

    setCurrentDate(newDate);
    const { formattedStartDate, formattedEndDate } =
      getStartAndEndDates(newDate);
    const eventData = await fetchEventsForRange(
      formattedStartDate,
      formattedEndDate
    );
    // Ensure eventData is always an array
    const safeEventData = Array.isArray(eventData) ? eventData : [];
    setEvents(safeEventData);
    // Reapply filters if any are active
    const filteredValues = applyFilters(safeEventData);
    setFilteredEvents(filteredValues);
  };

  // Helper function to calculate tooltip position with viewport boundary detection
  const calculateTooltipPosition = (
    clientX: number,
    clientY: number,
    isMobileDevice: boolean | null = false,
    isSelected: boolean = false
  ) => {
    const isMobileBool = isMobileDevice ?? false;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 10; // Padding from viewport edges
    const cursorOffset = 10; // Small offset to keep tooltip near cursor but not directly under it

    // Tooltip dimensions based on device and state
    const tooltipWidth = isSelected || isMobileBool ? 200 : 250;
    const tooltipHeight = 180; // Approximate tooltip height

    let x = clientX;
    let y = clientY;

    // For desktop hover (not selected, not mobile)
    if (!isSelected && !isMobileBool) {
      // Transform: translate(10%, -10%)
      // This means: right 10% of width, up 10% of height
      const transformOffsetX = tooltipWidth * 0.1; // 10% to the right
      const transformOffsetY = tooltipHeight * 0.1; // 10% up

      // Position tooltip near cursor - default is to the right and above
      x = clientX + cursorOffset;
      y = clientY - cursorOffset;

      // Calculate tooltip bounds after transform
      let tooltipLeft = x + transformOffsetX;
      let tooltipRight = tooltipLeft + tooltipWidth;
      let tooltipTop = y - transformOffsetY - tooltipHeight;
      let tooltipBottom = y - transformOffsetY;

      // Adjust horizontal position if tooltip would overflow viewport
      if (tooltipRight > viewportWidth - padding) {
        // Tooltip would overflow right edge - position to the left of cursor
        x = clientX - tooltipWidth - transformOffsetX - cursorOffset;
        tooltipLeft = x + transformOffsetX;
        tooltipRight = tooltipLeft + tooltipWidth;
      } else if (tooltipLeft < padding) {
        // Tooltip would overflow left edge - adjust to fit
        x = padding - transformOffsetX;
        tooltipLeft = x + transformOffsetX;
        tooltipRight = tooltipLeft + tooltipWidth;
      }

      // Recalculate vertical bounds after horizontal adjustment
      tooltipTop = y - transformOffsetY - tooltipHeight;
      tooltipBottom = y - transformOffsetY;

      // Adjust vertical position if tooltip would overflow viewport
      if (tooltipTop < padding) {
        // Tooltip would overflow top edge - position below cursor instead
        y = clientY + cursorOffset;
        tooltipTop = y - transformOffsetY - tooltipHeight;
        tooltipBottom = y - transformOffsetY;
      } else if (tooltipBottom > viewportHeight - padding) {
        // Tooltip would overflow bottom edge - adjust upward
        y = viewportHeight - tooltipHeight - transformOffsetY - padding;
        tooltipTop = y - transformOffsetY - tooltipHeight;
        tooltipBottom = y - transformOffsetY;
      }
    } else if (isSelected && !isMobileBool) {
      // Transform: translate(-10%, -120%)
      const transformOffsetX = tooltipWidth * 0.1; // 10% to the left
      const transformOffsetY = tooltipHeight * 1.2; // 120% up

      // Position tooltip near cursor
      x = clientX - cursorOffset;
      y = clientY - cursorOffset;

      // Calculate tooltip bounds after transform
      let tooltipLeft = x - transformOffsetX;
      let tooltipRight = tooltipLeft + tooltipWidth;
      let tooltipTop = y - transformOffsetY - tooltipHeight;
      let tooltipBottom = y - transformOffsetY;

      // Adjust horizontal position if tooltip would overflow viewport
      if (tooltipRight > viewportWidth - padding) {
        x = viewportWidth - tooltipWidth + transformOffsetX - padding;
        tooltipLeft = x - transformOffsetX;
        tooltipRight = tooltipLeft + tooltipWidth;
      } else if (tooltipLeft < padding) {
        x = padding + transformOffsetX;
        tooltipLeft = x - transformOffsetX;
        tooltipRight = tooltipLeft + tooltipWidth;
      }

      // Recalculate vertical bounds after horizontal adjustment
      tooltipTop = y - transformOffsetY - tooltipHeight;
      tooltipBottom = y - transformOffsetY;

      // Adjust vertical position if tooltip would overflow viewport
      if (tooltipTop < padding) {
        y = clientY + cursorOffset;
        tooltipTop = y - transformOffsetY - tooltipHeight;
        tooltipBottom = y - transformOffsetY;
      } else if (tooltipBottom > viewportHeight - padding) {
        y = viewportHeight - tooltipHeight - transformOffsetY - padding;
        tooltipTop = y - transformOffsetY - tooltipHeight;
        tooltipBottom = y - transformOffsetY;
      }
    } else if (isMobileBool) {
      // Transform: translate(-50%, -100%) - centers horizontally, positions above
      const transformOffsetX = tooltipWidth * 0.5; // 50% to center
      const transformOffsetY = tooltipHeight; // 100% up

      // Position tooltip centered on cursor horizontally, above vertically
      x = clientX;
      y = clientY - cursorOffset;

      let tooltipLeft = x - tooltipWidth / 2;
      let tooltipRight = x + tooltipWidth / 2;
      let tooltipTop = y - transformOffsetY - tooltipHeight;

      // Adjust horizontal position if tooltip would overflow viewport
      if (tooltipRight > viewportWidth - padding) {
        x = viewportWidth - tooltipWidth / 2 - padding;
        tooltipLeft = x - tooltipWidth / 2;
        tooltipRight = x + tooltipWidth / 2;
      } else if (tooltipLeft < padding) {
        x = tooltipWidth / 2 + padding;
        tooltipLeft = x - tooltipWidth / 2;
        tooltipRight = x + tooltipWidth / 2;
      }

      // Adjust vertical position if tooltip would overflow viewport
      if (tooltipTop < padding) {
        // Not enough space above, position below cursor
        y = clientY + cursorOffset;
      }
    }

    return { x, y };
  };

  const handleEventHover = (
    e: React.MouseEvent,
    event: Event = {} as Event,
    eventId: string = ""
  ) => {
    // If hovering a different event, clear the previous one first
    if (hoveredEventId && hoveredEventId !== eventId) {
      handleEventLeave();
    }

    if (event.eventDetails) {
      setHoveredEventId(eventId);
      handleTooltipContent(event.eventDetails);

      // Calculate tooltip position with viewport boundary detection
      const adjustedPosition = calculateTooltipPosition(
        e.clientX,
        e.clientY,
        isMobile,
        false // Not selected for hover
      );
      setTooltipPosition(adjustedPosition);
      return;
    }
    setHoveredEventId("");
    handleTooltipContent({});
  };

  const handleEventLeave = () => {
    setHoveredEventId("");
    handleTooltipContent({});
    setTooltipPosition({ x: 0, y: 0 });
  };

  const handleTouchStart = (event: Event, e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    const eventId = target.id;
    handleSelectedEventId(eventId);

    // Get the event element's bounding box for better positioning on mobile
    const eventElement = target.closest(".rbc-event") || target;
    const rect = eventElement.getBoundingClientRect();
    const touch = e.touches?.[0];

    if (touch && rect && rect.width > 0 && rect.height > 0) {
      // Calculate position: center horizontally on the event, prefer above
      const centerX = rect.left + rect.width / 2;
      const preferredY = rect.top; // Position above the event

      // Use the helper function to adjust for viewport boundaries
      const adjustedPosition = calculateTooltipPosition(
        centerX,
        preferredY,
        true, // isMobile
        true // isSelected
      );

      setTooltipPosition(adjustedPosition);
    } else if (touch) {
      // Fallback to touch coordinates with boundary detection
      const adjustedPosition = calculateTooltipPosition(
        touch.clientX,
        touch.clientY,
        true, // isMobile
        true // isSelected
      );
      setTooltipPosition(adjustedPosition);
    }

    if (event.eventDetails) {
      handleTooltipContent(event.eventDetails);
    }
  };

  const handleTouchEnd = () => {
    handleTooltipContent({});
    handleSelectedEventId("");
  };

  const getFormattedTime = (time?: string) => {
    if (!time) return "N/A";
    const date = parse(time, "HH:mm:ss", new Date());
    return isValid(date) ? format(date, "hh:mma") : "N/A";
  };

  const isObjectEmpty = (obj: Record<string, unknown>) =>
    Object.keys(obj).length === 0;

  // Memoize description to prevent duplicate rendering
  const memoizedDescription = useMemo(() => {
    return description;
  }, [description]);

  return (
    <div
      className={cn(
        "mx-auto sm:px-9 sm:pt-[38px] sm:pb-[42px] w-full",
        "xs:pt-8"
      )}
      onClick={handleTouchEnd}
      style={{
        backgroundColor: component_background_color || undefined,
      }}
    >
      {title && (
        <h2
          className={cn(
            "xs:text-heading-6-semibold xs:text-center",
            "col-span-2 px-[14px] sm:pl-[10px] sm:text-heading-5-semibold sm:text-left",
            "md:text-heading-4-semibold",
            "lg:text-new-heading-34-semibold",
            "xl:text-new-heading-42-semibold"
          )}
          style={{
            color: title_text_color || undefined,
          }}
        >
          {title}
        </h2>
      )}
      {(content || data?.content) && (
        <div
          dangerouslySetInnerHTML={{
            __html: content || data?.content || "",
          }}
          className="col-span-2 mt-[38px] px-[14px] sm:pl-[10px]"
        />
      )}
      <div
        className={cn(
          "xs:pt-[30px] flex flex-col items-start justify-center gap-[25px] px-[14px] ",
          "sm:mt-[42px] sm:px-[10px] sm:w-full sm:flex-row sm:justify-between"
        )}
      >
        <div
          className={cn(
            "flex",
            "xs:flex-col xs:gap-6 xs:w-full ",
            "sm:gap-[22px] sm:w-[80%] sm:flex-row",
            "md:w-[70%]"
          )}
        >
          <Select
            onValueChange={(value: string) =>
              handleFilterChange("courseName", value)
            }
          >
            <SelectTrigger
              className={cn(
                "flex items-center justify-between gap-[5px] w-full sm:shrink rounded-md border border-gray-350 bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none cursor-pointer",
                "text-heading-8-semibold",
                "py-2 px-3",
                "md:py-2 md:px-3",
                "lg:py-2 lg:px-3 lg:text-heading-7-semibold",
                "xl:py-2 xl:px-3 xl:text-heading-6-semibold"
              )}
              iconClassName="xs:h-6 xs:w-6 sm:h-4 sm:w-4 text-black"
            >
              <SelectValue placeholder="Select Course Name" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-[300px] overflow-y-auto w-[var(--radix-select-trigger-width)]">
              <SelectGroup>
                <SelectItem
                  value="SELECT"
                  className={cn(
                    "px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 cursor-pointer rounded-sm transition-colors duration-150",
                    "sm:text-heading-8-semibold",
                    "lg:text-heading-7-semibold",
                    "xl:text-heading-6-semibold"
                  )}
                >
                  Select Course Name
                </SelectItem>
                {eventFormOptions?.["courseName"]?.map((course: string) => (
                  <SelectItem
                    key={course}
                    value={course}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 focus:bg-blue-50 cursor-pointer rounded-sm transition-colors duration-150"
                  >
                    {course}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select
            onValueChange={(value) => handleFilterChange("semYear", value)}
          >
            <SelectTrigger
              className={cn(
                "flex items-center justify-between gap-[5px] w-full rounded-md border border-gray-350 bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none cursor-pointer",
                "text-heading-8-semibold",
                "py-2 px-3",
                "md:py-2 md:px-3",
                "lg:py-2 lg:px-3 lg:text-heading-7-semibold",
                "xl:py-2 xl:px-3 xl:text-heading-6-semibold"
              )}
              iconClassName="xs:h-6 xs:w-6 sm:h-4 sm:w-4 text-black"
            >
              <SelectValue placeholder="Select Semester" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-[300px] overflow-y-auto w-[var(--radix-select-trigger-width)]">
              <SelectGroup>
                <SelectItem
                  value="SELECT"
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 cursor-pointer rounded-sm transition-colors duration-150"
                >
                  Select Semester
                </SelectItem>
                {eventFormOptions?.["semYear"]?.map((sem: string) => (
                  <SelectItem
                    key={sem}
                    value={sem}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 focus:bg-blue-50 cursor-pointer rounded-sm transition-colors duration-150"
                  >
                    {sem}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select onValueChange={(value) => handleFilterChange("batch", value)}>
            <SelectTrigger
              className={cn(
                "flex items-center justify-between gap-[5px] w-full rounded-md border border-gray-350 bg-white transition-all duration-200 hover:border-gray-400 hover:shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none cursor-pointer",
                "text-heading-8-semibold",
                "py-2 px-3",
                "md:py-2 md:px-3",
                "lg:py-2 lg:px-3 lg:text-heading-7-semibold",
                "xl:py-2 xl:px-3 xl:text-heading-6-semibold"
              )}
              iconClassName="xs:h-6 xs:w-6 sm:h-4 sm:w-4 text-black"
            >
              <SelectValue placeholder="Select Batch" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-[300px] overflow-y-auto w-[var(--radix-select-trigger-width)]">
              <SelectGroup>
                <SelectItem
                  value="SELECT"
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 cursor-pointer rounded-sm transition-colors duration-150"
                >
                  Select Batch
                </SelectItem>
                {eventFormOptions?.["batch"]?.map((value: string) => (
                  <SelectItem
                    key={value}
                    value={value}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 focus:bg-blue-50 cursor-pointer rounded-sm transition-colors duration-150"
                  >
                    {value}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {button_text && (
          <Button
            onClick={handleSubmit}
            className={cn(
              "flex gap-3 shrink-0 normal-case leading-normal transition-opacity active:opacity-70 border",
              "xs:w-full xs:text-heading-8-semibold",
              "sm:w-fit sm:gap-3 sm:py-2 sm:px-3 sm:text-heading-8-semibold",
              "md:py-2 md:px-3",
              "lg:py-2 lg:px-3 lg:text-heading-7-semibold",
              "xl:py-2 xl:px-3"
            )}
            style={{
              backgroundColor: button_bg_color || "#3B82F6",
              color: button_text_color || "#FFFFFF",
              borderColor: button_bg_color || "#3B82F6",
              borderWidth: "1px",
            }}
          >
            {button_text}
            {button_icon && (
              <img
                src={button_icon}
                alt="Button icon"
                className={cn(
                  "w-6 h-6",
                  "sm:w-6 sm:h-6",
                  "md:w-7 md:h-7",
                  "lg:w-8 lg:h-8",
                  "xl:w-9 xl:h-9"
                )}
              />
            )}
          </Button>
        )}
      </div>
      <div
        className={cn(
          "sm:my-[42px] sm:h-[640px] w-full pb-0 sm:px-[10px]",
          "xs:mt-[48px] xs:mb-0 xs:px-5 xs:h-[500px]"
        )}
        {...(!isMobile && { onMouseLeave: handleEventLeave })}
        onClick={handleTouchEnd}
      >
        {!isObjectEmpty(tooltipContent as Record<string, unknown>) && (
          <div className="relative">
            <div
              className={`${
                selectedEventId && isMobile
                  ? "fixed w-[200px]"
                  : selectedEventId
                  ? "absolute w-[200px]"
                  : "fixed w-[250px]"
              } z-100 rounded-md px-3 py-2 text-sm shadow-md`}
              style={{
                backgroundColor: tooltip_color || "#1F2937",
                color: tooltip_text_color || "#FFFFFF",
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: "1.4",
                pointerEvents: "none",
                zIndex: "100",
                transform:
                  selectedEventId && isMobile
                    ? "translate(-50%, -100%)" // Center horizontally, position above on mobile
                    : selectedEventId
                    ? "translate(-10%, -120%)"
                    : "translate(10%, -10%)",
                maxWidth: isMobile ? "calc(100vw - 20px)" : undefined, // Ensure tooltip doesn't exceed viewport on mobile
              }}
            >
              <p>Course:- {(tooltipContent as any)?.courseName}</p>
              <p>Subject:- {(tooltipContent as any)?.subjectName}</p>
              <p>
                Date:-{" "}
                {format((tooltipContent as any)?.startDate || "", "dd/MM/yyyy")}
              </p>
              <p>
                Time:- {getFormattedTime((tooltipContent as any)?.startTime)} to{" "}
                {getFormattedTime((tooltipContent as any)?.endTime)}
              </p>
              <p>Batch:- {(tooltipContent as any)?.batch}</p>
              <p>Semester:- {(tooltipContent as any)?.semYear}</p>
            </div>
          </div>
        )}

        <Calendar
          localizer={localizer}
          events={Array.isArray(filteredEvents) ? filteredEvents : []}
          selectable={false}
          startAccessor="start"
          endAccessor="end"
          views={[CALENDAR_VIEWS.MONTH, CALENDAR_VIEWS.DAY]}
          view={view}
          onView={handleViewChange}
          date={currentDate}
          step={30}
          timeslots={2}
          className="live-lecture-calendar"
          dayPropGetter={() => ({
            className: `${
              view === CALENDAR_VIEWS.MONTH ? "bg-transparent" : ""
            }`,
            style: {
              color:
                view === CALENDAR_VIEWS.DAY
                  ? day_text_color || undefined
                  : undefined,
            },
          })}
          slotPropGetter={(date: Date) => {
            if (view === CALENDAR_VIEWS.DAY) {
              return {
                style: {
                  minHeight: "44px",
                  height: "44px",
                },
              };
            }
            return {};
          }}
          showAllEvents
          eventPropGetter={() => {
            return {
              className: `text-[12px] overflow-hidden !p-0`,
              style: {
                backgroundColor: event_color || "#10B981",
                color: event_text_color || "#1F2937",
              },
            };
          }}
          components={{
            timeGutterHeader: () => (
              <div className="flex items-center">All-Day</div>
            ),
            header: ({ date, label }: { date: Date; label?: string }) => {
              return (
                <div
                  className={cn(
                    "flex items-center justify-center w-full",
                    "xs:text-heading-8-bold xs:py-2",
                    view === CALENDAR_VIEWS.DAY
                      ? "sm:h-[60px] font-semibold"
                      : "sm:h-[60px]"
                  )}
                  style={{
                    backgroundColor: day_section_color || undefined,
                    color: day_text_color || undefined,
                  }}
                >
                  {view === CALENDAR_VIEWS.DAY
                    ? format(date, "EEEE")
                    : label || format(date, "EEE")}
                </div>
              );
            },
            toolbar: ({ date }: { date: Date }) => {
              return (
                <div className="flex flex-col items-center justify-between w-full">
                  <div
                    className={cn(
                      "flex items-center w-full justify-between pb-6 pt-[10px]",
                      "xs:flex-col xs:gap-4 xs:pb-6",
                      "sm:pb-6 sm:flex-row"
                    )}
                    onMouseOver={(e) => e.stopPropagation()}
                  >
                    <div
                      className={cn(
                        "xs:grid xs:w-full xs:gap-[2px] xs:grid-cols-2",
                        "sm:flex sm:gap-[2px] sm:w-fit"
                      )}
                    >
                      <button
                        className={cn(
                          "flex items-center justify-center rounded-tl-md rounded-bl-md border border-[#b8b8b8] sm:bg-[#f3f4f6] text-[22px] hover:bg-[#eaecf0]",
                          "xs:w-full xs:py-2",
                          "sm:w-[88px] sm:py-2",
                          "xl:py-2"
                        )}
                        onClick={() => handleNavigate(NAVIGATE.PREV)}
                        style={{
                          color: navigate_button_color || undefined,
                        }}
                      >
                        {left_navigate_icon ? (
                          <img
                            src={left_navigate_icon}
                            alt="Previous"
                            className={cn("size-6", "xl:size-7")}
                          />
                        ) : (
                          <ChevronLeft className={cn("size-6", "xl:size-7")} />
                        )}
                      </button>

                      <button
                        className={cn(
                          "flex items-center justify-center rounded-tr-md rounded-br-md border border-[#b8b8b8] sm:bg-[#f3f4f6] text-[22px] hover:bg-[#eaecf0]",
                          "xs:w-full xs:py-2",
                          "sm:w-[88px] sm:py-2",
                          "xl:py-2"
                        )}
                        onClick={() => handleNavigate(NAVIGATE.NEXT)}
                        style={{
                          color: navigate_button_color || undefined,
                        }}
                      >
                        {right_navigate_icon ? (
                          <img
                            src={right_navigate_icon}
                            alt="Next"
                            className={cn("size-6", "xl:size-7")}
                          />
                        ) : (
                          <ChevronRight className={cn("size-6", "xl:size-7")} />
                        )}
                      </button>
                    </div>

                    <span className="text-heading-6-semibold sm:text-[25px]">
                      {view === CALENDAR_VIEWS.DAY
                        ? format(date, "MMMM dd, yyyy")
                        : format(date, "MMMM yyyy")}
                    </span>

                    <div
                      className={cn(
                        "flex rounded-md overflow-hidden",
                        "xs:w-full",
                        "sm:w-fit"
                      )}
                    >
                      <button
                        className={cn(
                          `rounded-l-md text-heading-8-semibold`,
                          "xs:w-full xs:py-2 ",
                          "sm:px-[18px] sm:py-2 sm:w-fit ",
                          "lg:text-heading-7-semibold",
                          "xl:text-heading-6-semibold",
                          {
                            "border-r-[6px]": view === CALENDAR_VIEWS.MONTH,
                          }
                        )}
                        onClick={() => handleViewChange(CALENDAR_VIEWS.MONTH)}
                        style={{
                          backgroundColor:
                            view === CALENDAR_VIEWS.MONTH
                              ? view_button_active_bg_color || "#3B82F6"
                              : view_button_bg_color || "#F3F4F6",
                          color:
                            view === CALENDAR_VIEWS.MONTH
                              ? view_button_active_text_color || "#FFFFFF"
                              : view_button_text_color || "#374151",
                          borderRightColor:
                            view === CALENDAR_VIEWS.MONTH
                              ? active_bar_color || "#3B82F6"
                              : undefined,
                        }}
                      >
                        Month
                      </button>

                      <button
                        className={cn(
                          `text-heading-8-semibold`,
                          "xs:w-full xs:py-2",
                          "sm:px-[18px] sm:py-2 sm:w-fit ",
                          "lg:text-heading-7-semibold",
                          "xl:text-heading-6-semibold",
                          {
                            "border-r-[6px]": view === CALENDAR_VIEWS.DAY,
                          }
                        )}
                        onClick={() => handleViewChange(CALENDAR_VIEWS.DAY)}
                        style={{
                          backgroundColor:
                            view === CALENDAR_VIEWS.DAY
                              ? view_button_active_bg_color || "#3B82F6"
                              : view_button_bg_color || "#F3F4F6",
                          color:
                            view === CALENDAR_VIEWS.DAY
                              ? view_button_active_text_color || "#FFFFFF"
                              : view_button_text_color || "#374151",
                          borderRightColor:
                            view === CALENDAR_VIEWS.DAY
                              ? active_bar_color || "#3B82F6"
                              : undefined,
                        }}
                      >
                        Day
                      </button>
                    </div>
                  </div>
                  {view === CALENDAR_VIEWS.DAY && (
                    <div
                      className={cn(
                        "flex items-center justify-center w-full",
                        "sm:h-[60px] font-semibold",
                        "xs:text-heading-8-bold xs:py-2 "
                      )}
                      style={{
                        backgroundColor: day_section_color || undefined,
                        color: day_text_color || undefined,
                      }}
                    >
                      {format(currentDate, "EEEE")}
                    </div>
                  )}
                </div>
              );
            },
            event: ({ event }: { event: any }) => {
              const typedEvent = event as Event;
              const isStartValid =
                typedEvent?.start && isValid(typedEvent.start);
              const formattedStart = isStartValid
                ? format(typedEvent.start, "hh:mma")
                : "";
              const eventTitle = typedEvent?.title || "";
              eventId = eventId + 1;
              const currentEventId = String(eventId);
              return (
                <div
                  id={currentEventId}
                  {...(!isMobile && {
                    onMouseEnter: (e) => {
                      e.stopPropagation();
                      handleEventHover(e, typedEvent, currentEventId);
                    },
                    onMouseLeave: (e) => {
                      e.stopPropagation();
                      handleEventLeave();
                    },
                  })}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    handleTouchStart(typedEvent, e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="h-full p-1"
                  style={{
                    backgroundColor:
                      selectedEventId === currentEventId
                        ? event_color || "#10B981"
                        : event_color || "#10B981",
                    color: event_text_color || "#1F2937",
                    opacity: selectedEventId === currentEventId ? 0.8 : 1,
                  }}
                >
                  {`${formattedStart} ${eventTitle}`}
                </div>
              );
            },
          }}
        />
      </div>
      {memoizedDescription && (
        <div
          key="live-lecture-description"
          className={cn(
            "text-secondary-main flex items-start ",
            "xs:pt-6 xs:pb-8 xs:px-5 xs:text-center",
            "sm:py-0 sm:text-left"
          )}
          dangerouslySetInnerHTML={{ __html: memoizedDescription }}
        ></div>
      )}
    </div>
  );
};

export default LiveLecture1;
